import { Component } from '@theme/component';

/** Featured collection tabs, with pointer and keyboard selection. */
class CollectionTabs extends Component {
  #listeners;

  connectedCallback() {
    super.connectedCallback();
    this.#listeners?.abort();
    this.#listeners = new AbortController();
    this.addEventListener('click', this.#handleClick, { signal: this.#listeners.signal });
    this.addEventListener('keydown', this.#handleKeydown, { signal: this.#listeners.signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listeners?.abort();
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[role="tab"]') : null;
    if (button instanceof HTMLButtonElement && this.contains(button)) this.#select(button);
  };

  /** @param {KeyboardEvent} event */
  #handleKeydown = (event) => {
    if (!(event.target instanceof HTMLButtonElement) || event.target.getAttribute('role') !== 'tab') return;
    const buttons = [...this.querySelectorAll('[role="tab"]')];
    const index = buttons.indexOf(event.target);
    const direction = getComputedStyle(this).direction === 'rtl' ? -1 : 1;
    let next;
    if (event.key === 'ArrowRight') next = (index + direction + buttons.length) % buttons.length;
    else if (event.key === 'ArrowLeft') next = (index - direction + buttons.length) % buttons.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = buttons.length - 1;
    else return;
    event.preventDefault();
    const button = buttons[next];
    if (!(button instanceof HTMLButtonElement)) return;
    this.#select(button);
    button.focus({ preventScroll: true });
  };

  /** @param {HTMLButtonElement} selectedButton */
  #select(selectedButton) {
    const panelId = selectedButton.getAttribute('aria-controls');
    const panels = [...this.querySelectorAll('[role="tabpanel"]')];
    if (!panels.some((panel) => panel.id === panelId)) return;
    for (const button of this.querySelectorAll('[role="tab"]')) {
      const selected = button === selectedButton;
      button.classList.toggle('active', selected);
      button.setAttribute('aria-selected', String(selected));
      button.setAttribute('tabindex', selected ? '0' : '-1');
    }
    for (const panel of panels) {
      const selected = panel.id === panelId;
      panel.classList.toggle('active', selected);
      panel.hidden = !selected;
    }
  }
}

if (!customElements.get('collection-tabs')) customElements.define('collection-tabs', CollectionTabs);
